import { WithHiddenListBox } from '@components/design';
import React, { useEffect, useMemo, useState } from 'react';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';

import { FactRevenue, FeatureOption, getPageTypeWithPageName } from '@utils/pageConfig/Types';

import { useAnalytics } from '@ecosystem/newron-client-lib';
import { ColumnConfig } from '@components/design/migrated/Table';
import { useSelector } from 'react-redux';
import { CL_DOUBLE_BAR_CHART_TITLE } from '@pages/Utilities';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import FeatureSelector from '../feature-selector/FeatureSelector';
import { FeatureSelectorType } from '../feature-selector/Types';
import {
  constructCurrencyLabel,
  formatLabelIntoRevenuePriceTitle,
  isRevenueType,
} from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { RetailerClientStateKeys } from '@state/reducers/Reducers';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

interface ChartPickersProps {
  kpisConfig: ColumnConfig[];
  sorting: SortingOptions;
  updateSorting: (options: SortingOptions) => void;
  getDisplayName: (headerKey: string, volume: string) => string;
  hideSorting?: boolean;
  featureSelectorType?: FeatureSelectorType;
  featureHandler?: () => void;
  chartName?: string;
  onKpiSelectHandler?: () => void;
  onSortHandler?: () => void;
  factRevenue: FactRevenue;
  volume?: string;
  retailerClientStateKey?: RetailerClientStateKeys;
  isLoading: boolean;
}

export const ChartPickers = ({
  kpisConfig,
  sorting,
  updateSorting,
  getDisplayName,
  hideSorting,
  featureSelectorType,
  featureHandler,
  chartName = CL_DOUBLE_BAR_CHART_TITLE,
  onKpiSelectHandler,
  onSortHandler,
  factRevenue,
  volume = DEFAULT_VOLUME,
  retailerClientStateKey = 'retailerClient',
  isLoading,
}: ChartPickersProps): JSX.Element => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state[retailerClientStateKey]);
  const currencyLabel = constructCurrencyLabel(factRevenue);

  const [kpiValue, setKpiValue] = useState<string | null>(sorting.headerKey);
  const [sortValue, setSortValue] = useState<string | null>(`${sorting.sortKey}|${sorting.sortDir}`);

  // have to check if the kpi is revenue or volume related to determine if we need to display the label or not
  const kpiOptions = kpisConfig.map((kpi) => ({
    label: isRevenueType(kpi.headerKey)
      ? formatLabelIntoRevenuePriceTitle(getDisplayName(kpi.headerKey, volume), currencyLabel)
      : getDisplayName(kpi.headerKey, volume),
    value: kpi.headerKey,
  }));
  const handleChartInteractionTracking = (elementName: ChartElementName, optionChosen: string) => {
    //Start Chart Interaction Tracking
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName,
      elementType: ChartElementType.FILTER,
      optionChosen,
      chartName,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
    //End Chart Interaction Tracking
  };

  const sortOptions = kpisConfig
    .find((kpi) => kpi.headerKey === sorting.headerKey)
    ?.sortFields?.map((sortField) => ({
      label: `${sortField.label} ${sortField.sortDirection === SortDirection.asc ? 'Ascending' : 'Descending'}`,
      value: `${sortField.sortKey}|${sortField.sortDirection}`,
    })) || [{ label: 'Absolute Descending', value: '|desc' }];

  useEffect(() => {
    const selectedOption = kpiOptions.find((o) => o.value === kpiValue);
    if (selectedOption && selectedOption.value !== sorting.headerKey) {
      updateSorting({
        ...sorting,
        headerKey: selectedOption.value,
      });
      handleChartInteractionTracking(
        ChartElementName.KPI,
        isRevenueType(selectedOption.value)
          ? formatLabelIntoRevenuePriceTitle(getDisplayName(selectedOption.value, volume), currencyLabel)
          : getDisplayName(selectedOption.value, volume),
      );
      onKpiSelectHandler?.();
    }
  }, [kpiValue]);

  useEffect(() => {
    const selectedOption = sortOptions?.find((o) => o.value === sortValue);
    if (selectedOption && selectedOption.value !== `${sorting.sortKey}|${sorting.sortDir}`) {
      const [sortKey, sortDir] = selectedOption.value.split('|');

      updateSorting({
        ...sorting,
        sortDir: sortDir as SortDirection,
        sortKey,
        label: sortKey || 'Absolute',
      });
      onSortHandler?.();
      handleChartInteractionTracking(
        ChartElementName.SORT_BY,
        `${sorting.label} ${sorting.sortDir === SortDirection.asc ? 'Ascending' : 'Descending'}`,
      );
    }
  }, [sortValue]);

  const getOptionLabel = (options: any[], value: string | null) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowLabelOption = useMemo(() => getOptionLabel(kpiOptions, kpiValue), [kpiOptions, kpiValue]);
  const memoizedSelectedSortedByLabelOption = useMemo(
    () => getOptionLabel(sortOptions, sortValue),
    [sortOptions, sortValue],
  );

  return (
    <div>
      <div className="flex-row flex-wrap">
        <div
          data-testid={'kpi-selector'}
          key={`${kpiOptions}-${kpiValue}`} //NOSONAR typescript:S6551
        >
          <WithHiddenListBox
            isDisabled={isLoading}
            label={'Show'}
            options={kpiOptions}
            value={memoizedSelectedShowLabelOption}
            onChange={({ value }) => {
              setKpiValue(value);
            }}
          />
        </div>
        {featureSelectorType && (
          <div
            style={{ width: '400px' }}
            key={`${featureSelectorType.isDisabled}-${featureSelectorType.featureFilters}`} //NOSONAR typescript:S6551
          >
            <FeatureSelector
              isDisabled={featureSelectorType.isDisabled}
              label={'Show performance within'}
              featureValues={featureSelectorType.featureFilters}
              featureOptions={(feat: FeatureOption) => featureSelectorType.setFeature(feat)}
              selectedOption={featureSelectorType.feature}
              withoutDiv={true}
              featureOnChange={featureHandler}
              chartName={chartName}
            />
          </div>
        )}
        {!hideSorting && (
          <div
            data-testid={'sortBy-kpi-selector'}
            key={`${sortOptions}-${sortValue}`} //NOSONAR typescript:S6551
          >
            <WithHiddenListBox
              isDisabled={isLoading}
              label={'sorted by'}
              options={sortOptions}
              value={memoizedSelectedSortedByLabelOption}
              onChange={({ value }) => {
                setSortValue(value);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
